import {useMemo} from 'react'

import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES} from '@contentful/rich-text-types'

import {Box, BreakpointSize, Grid, Hero, InlineLink, useWindowSize, Text} from '@primer/react-brand'

import {useRoutePayload} from '@github-ui/react-core/use-route-payload'

import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {ContentfulLogoSuite} from '@github-ui/swp-core/components/contentful/ContentfulLogoSuite'
import {ContentfulSubnav} from '@github-ui/swp-core/components/contentful/ContentfulSubnav'
import type {PrimerComponentLogoSuite} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentLogoSuite'
import type {PrimerComponentSubnav} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentSubnav'

import type {GenericSectionWithIds, GenericContent} from '../../../../../brand/lib/types/contentful'

import {cohortFunnelBuilder} from '../../../../../lib/analytics'

import {HeroVideo} from './HeroVideo'

type Props = {
  contentfulContent: GenericSectionWithIds
  subnav?: PrimerComponentSubnav
}

export function HeroSection(props: Props) {
  const {subnav, contentfulContent} = props

  const {featuresCopilotHero} = contentfulContent.ids as {
    featuresCopilotHero: GenericContent
  }

  const {label, heading, links, media, ctas, content} = featuresCopilotHero.fields

  const defaultCtas = ctas?.find(ctaGroup => ctaGroup.fields.condition === 'None')
  const primaryCta = defaultCtas?.fields.primary || links?.at(0)
  const secondaryCta = defaultCtas?.fields.secondary || links?.at(1)

  const tertiaryCta = content?.find(item => item.fields.id === 'featuresCopilotHeroTertiaryCta') as
    | GenericContent
    | undefined

  const heroVideoLarge = media?.find(item => item.fields.id === 'heroVideoLarge')
  const heroVideoSmall = media?.find(item => item.fields.id === 'heroVideoSmall')
  const heroPosterLarge = media?.find(item => item.fields.id === 'heroPosterLarge')
  const heroPosterSmall = media?.find(item => item.fields.id === 'heroPosterSmall')

  const logosComponent = contentfulContent.fields.content?.find(
    item => item.sys.contentType.sys.id === 'primerComponentLogoSuite',
  ) as PrimerComponentLogoSuite | undefined

  const {cft} = useRoutePayload<{cft: string}>()
  const withCft = cohortFunnelBuilder(cft)

  const windowSize = useWindowSize()
  const isDesktopView = useMemo(
    () =>
      [BreakpointSize.MEDIUM, BreakpointSize.LARGE, BreakpointSize.XLARGE, BreakpointSize.XXLARGE].includes(
        windowSize.currentBreakpointSize!,
      ),
    [windowSize.currentBreakpointSize],
  )

  return (
    <div className="position-relative">
      <Box className="lp-SubNav-spacer" />

      {subnav ? <ContentfulSubnav component={subnav} className="lp-Hero-subnav lp-Hero-subnav--highContrast" /> : null}

      <section id="hero" className="lp-Section lp-Section--compact lp-Section--hero">
        <Grid>
          <Grid.Column span={12}>
            <Hero data-hpc align="center" className="lp-Hero">
              {label ? (
                <div className="lp-ConicGradientBorder lp-ConicGradientBorder-label lp-ConicGradientBorder-hero d-inline-block mb-4">
                  <Hero.Label
                    size="large"
                    color="purple-red"
                    className="lp-ConicGradientBorder-label-inner"
                    style={{minHeight: '30px', height: 'auto', marginBottom: '0'}}
                  >
                    {label.fields.text}
                  </Hero.Label>
                </div>
              ) : null}

              {heading ? (
                <Hero.Heading size="1" weight="bold" className="lp-Hero-heading lp-Hero-heading-mask">
                  {heading}
                </Hero.Heading>
              ) : null}

              <div className="lp-Hero-ctaButtons">
                {primaryCta && (
                  <Hero.PrimaryAction
                    href={primaryCta.fields.href}
                    hasArrow={false}
                    {...getAnalyticsEvent({
                      action: primaryCta.fields.text,
                      tag: 'button',
                      context: 'CTAs',
                      location: 'hero',
                    })}
                  >
                    {primaryCta.fields.text}
                  </Hero.PrimaryAction>
                )}

                {secondaryCta ? (
                  <Hero.SecondaryAction
                    href={withCft(secondaryCta.fields.href)}
                    hasArrow={false}
                    variant="subtle"
                    {...getAnalyticsEvent({
                      action: secondaryCta.fields.text,
                      tag: 'button',
                      context: 'CTAs',
                      location: 'hero',
                    })}
                  >
                    {secondaryCta.fields.text}
                  </Hero.SecondaryAction>
                ) : null}
              </div>

              {isDesktopView && tertiaryCta && tertiaryCta.fields.text ? (
                <Text weight="medium" style={{marginTop: '1rem'}}>
                  {documentToReactComponents(tertiaryCta.fields.text, {
                    renderNode: {
                      [BLOCKS.PARAGRAPH]: (_, children) => [children],
                      [INLINES.HYPERLINK]: (node, children) => (
                        <InlineLink
                          href={node.data.uri}
                          className="tertiary-cta"
                          {...getAnalyticsEvent({
                            action: documentToPlainTextString(node, ' '),
                            tag: 'link',
                            context: 'CTAs',
                            location: 'hero',
                          })}
                        >
                          {children}
                        </InlineLink>
                      ),
                    },
                  })}
                </Text>
              ) : null}
            </Hero>

            {heroVideoLarge ? (
              <HeroVideo
                description={heroVideoLarge.fields.description}
                posterSrcLarge={heroPosterLarge?.fields.asset.fields.file.url}
                posterSrcSmall={heroPosterSmall?.fields.asset.fields.file.url}
                videoSrcLarge={heroVideoLarge.fields.asset.fields.file.url}
                videoSrcSmall={heroVideoSmall?.fields.asset.fields.file.url}
              />
            ) : null}

            {logosComponent ? <ContentfulLogoSuite component={logosComponent} className="lp-LogoSuite" /> : null}
          </Grid.Column>
        </Grid>

        <div className="lp-Hero-cover" />
      </section>
    </div>
  )
}

try{ HeroSection.displayName ||= 'HeroSection' } catch {}